<template>
  <vs-dropdown
    class="w-full h-full cursor-pointer interactive-pill"
    vs-custom-content
    vs-trigger-click
  >
    <vs-chip
      v-if="status"
      class="cursor-pointer w-full"
      :color="chipColor">
      <feather-icon
        :icon="chipIcon"
        svgClasses="h-5 w-5 mr-1" />
      <span>
          {{ chipText }}
        </span>

      <feather-icon
        icon="ChevronDownIcon"
        svgClasses="h-5 w-5 ml-1" />
    </vs-chip>

    <vs-dropdown-menu
      class="waiting-list-cell-renderer-status"
      :class="onPopup ? 'on-popup' : ''"
      style="width: 200px"
    >
      <div
        class="status-dropdown-option interactive-pill"
        v-for="(status, index) in statusOptionsAvailable"
        :key="status"
        @click="confirmChangeStatusOtherThanAttended(status)"
      >
        <vs-chip
          style="float: none"
          class="cursor-pointer w-full"
          :class="index > 0 ? 'mt-3' : ''"
          :color="getChipColor(status)"
          >
          <feather-icon
            :icon="getChipIcon(status)"
            svgClasses="h-5 w-5 mr-1" />
          <span>
          {{ getChipText(status) }}
        </span>
        </vs-chip>
      </div>
    </vs-dropdown-menu>

    <vs-prompt
      color="success"
      :title="$t('$ConfirmChangeStatus.$Attended.Title')"
      :acceptText="$t('$ConfirmChangeStatus.$Attended.MarkAsDoneButton')"
      :cancelText="$t('$ConfirmChangeStatus.$Attended.SendReviewButton')"
      buttonAccept="accept-fill"
      buttonCancel="accept-flat"
      @accept="changeStatus($enums.WaitingList.STATUS.ATTENDED)"
      @cancel="changeStatus($enums.WaitingList.STATUS.ATTENDED, true)"
      :active.sync="showConfirmAttendedStatus">
      {{ $t('$ConfirmChangeStatus.$Attended.Text') }}
    </vs-prompt>
  </vs-dropdown>
</template>

<script>
import waitingListStatusMixin from '@/views/modules/waiting-list/waiting-list-status.mixin';

export default {
  name: 'WaitingListCellRendererStatus',
  i18n: {
    messages: {
      en: {
        $ConfirmChangeStatus: {
          $Attended: {
            Title: 'Complete turn',
            Text: 'How would you like to close this turn?',
            MarkAsDoneButton: 'Mark as done',
            SendReviewButton: 'Send review request',
            CancelButton: 'Cancel',
          },
          $Others: {
            Title: 'Update turn status to {status}',
            Text: 'Are you sure you want to change the turn status to {status}?',
            ConfirmButton: 'Yes, update',
            CancelButton: 'No, close',
          },
        },
      },
    },
  },
  mixins: [waitingListStatusMixin],
  props: {
    isAgGridComponent: {
      type: Boolean,
      default: true,
    },
    customerPayload: {
      type: [Object, null],
      default: () => null,
    },
    onPopup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showConfirmAttendedStatus: false,
    };
  },
  computed: {
    customerData() {
      if (this.customerPayload) {
        return this.customerPayload;
      }

      return this.params && this.params.data
        ? this.params.data
        : null;
    },
    statusOptionsAvailable() {
      return this.statusOptions.filter(
        (option) => this.status !== option,
      );
    },
    status() {
      if (this.isAgGridComponent) {
        return this.params.value;
      }

      return this.customerData && this.customerData.status
        ? this.customerData.status
        : null;
    },
  },
  methods: {
    confirmChangeStatus(value) {
      if (value !== this.$enums.WaitingList.STATUS.ATTENDED) {
        this.confirmChangeStatusOtherThanAttended(value);
      } else {
        this.confirmChangeStatusToAttended();
      }
    },
    confirmChangeStatusToAttended() {
      // Close the dropdown before showing the confirmation modal
      this.$nextTick(() => {
        document.body.click();
      });

      this.showConfirmAttendedStatus = true;
    },
    confirmChangeStatusOtherThanAttended(value) {
      // Close the dropdown before showing the confirmation modal
      this.$nextTick(() => {
        document.body.click();
      });

      // Check if this is a rejoin scenario
      // Moving from completed (Cancelled/Served) to any active queue state
      const currentStatus = this.customerData.status;
      const completedStatuses = [
        this.$enums.WaitingList.STATUS.CANCELED,
        this.$enums.WaitingList.STATUS.ATTENDED,
      ];
      const activeQueueStatuses = [
        this.$enums.WaitingList.STATUS.WAITING,
        this.$enums.WaitingList.STATUS.READY_NEXT,
        this.$enums.WaitingList.STATUS.ACTIVE,
      ];
      
      const isRejoin = 
        completedStatuses.includes(currentStatus) &&
        activeQueueStatuses.includes(value);

      const title = isRejoin
        ? 'Rejoin the queue?'
        : this.$t('$ConfirmChangeStatus.$Others.Title', {
            status: this.$t(`$Enums.WaitingList.STATUS.${value}`),
          });

      const text = isRejoin
        ? 'This customer will be placed at the end of the queue for fairness. They will receive a queue confirmation notification with their new position.'
        : this.$t('$ConfirmChangeStatus.$Others.Text', {
            status: this.$t(`$Enums.WaitingList.STATUS.${value}`),
          });

      this.$showConfirmDialog({
        title,
        text,
        color: this.getChipColor(value),
        accept: () => this.changeStatus(value),
        cancel: () => {},
        acceptText: isRejoin ? 'Yes, rejoin queue' : this.$t('$ConfirmChangeStatus.$Others.ConfirmButton'),
        cancelText: isRejoin ? 'Cancel' : this.$t('$ConfirmChangeStatus.$Others.CancelButton'),
      });
    },
    changeStatus(status, review = true) {
      // Use the queue system for optimistic updates
      if (this.isAgGridComponent) {
        // Call parent's queue method instead of triggering full refresh
        this.params.context.componentParent.queueStatusChange(
          this.customerData,
          status,
          review,
        );
      } else {
        // For non-AgGrid usage (like in popup), emit event for parent to handle
        this.$emit('changed', { ...this.customerData, status, review });
      }
    },
  },
};
</script>

<style lang="scss">
.waiting-list-cell-renderer-status.on-popup {
  z-index: 54000 !important;
}
</style>
